import { useMutation } from "@tanstack/react-query"
import { useCallback, useRef } from "react"

export const useDebouncedMutations = (debounceDelay = 1500) => {
	const pendingTimeouts = useRef<Record<string, NodeJS.Timeout>>({})
	const activeControllers = useRef<Record<string, AbortController>>({})

	const createDebouncedMutation = useCallback(
		<TData, TError, TVariables>(
			mutationKey: string,
			mutationFn: (variables: TVariables) => Promise<TData>
		) => {
			return useMutation<TData, TError, TVariables>({
				mutationFn: async (variables: TVariables) => {
					// Cancel any existing timeout for this mutation
					if (pendingTimeouts.current[mutationKey]) {
						clearTimeout(pendingTimeouts.current[mutationKey])
					}

					// Abort any existing request for this mutation
					if (activeControllers.current[mutationKey]) {
						activeControllers.current[mutationKey].abort()
					}

					// Create a new AbortController for this request
					const controller = new AbortController()
					activeControllers.current[mutationKey] = controller

					// Return a promise that resolves after the debounce delay
					return new Promise<TData>((resolve, reject) => {
						const timeoutId = setTimeout(async () => {
							try {
								// Check if this request was cancelled
								if (controller.signal.aborted) {
									reject(new Error("Request was cancelled"))
									return
								}

								// Execute the mutation
								const result = await mutationFn(variables)

								// Clean up the controller after successful execution
								delete activeControllers.current[mutationKey]
								delete pendingTimeouts.current[mutationKey]

								resolve(result)
							} catch (error) {
								// Clean up the controller after error
								delete activeControllers.current[mutationKey]
								delete pendingTimeouts.current[mutationKey]

								reject(error)
							}
						}, debounceDelay)

						pendingTimeouts.current[mutationKey] = timeoutId

						// Handle abortion
						controller.signal.addEventListener("abort", () => {
							clearTimeout(timeoutId)
							delete pendingTimeouts.current[mutationKey]
							reject(new Error("Request was cancelled"))
						})
					})
				}
			})
		},
		[debounceDelay]
	)

	return { createDebouncedMutation }
}
